import React, { useState, useEffect, useCallback } from 'react';
import './UserManagement.css';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [initialized, setInitialized] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    role: 'admin',
    permissions: ['read', 'write'],
    password: '',
    passwordConfirm: '',
    newPassword: '',
    newPasswordConfirm: ''
  });

  const roles = [
    { value: 'admin', label: 'Administrador', color: 'var(--javiland-gold)' },
    { value: 'super_admin', label: 'Super Administrador', color: 'var(--javiland-wine)' },
    { value: 'moderator', label: 'Moderador', color: 'var(--javiland-green)' }
  ];

  const availablePermissions = [
    { value: 'read', label: 'Lectura', icon: 'fas fa-eye' },
    { value: 'write', label: 'Escritura', icon: 'fas fa-edit' },
    { value: 'delete', label: 'Eliminación', icon: 'fas fa-trash' },
    { value: 'export', label: 'Exportación', icon: 'fas fa-download' },
    { value: 'admin', label: 'Administración', icon: 'fas fa-cog' },
    { value: 'users', label: 'Gestión de Usuarios', icon: 'fas fa-users-cog' }
  ];

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('javiland_token');
      
      const response = await fetch(`${API_BASE}/admin/users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.message || 'Error al cargar usuarios');
      }
    } catch (error) {
      setError('Error de conexión: ' + error.message);
    } finally {
      setLoading(false);
      setInitialized(true);
    }
  }, []);

  useEffect(() => {
    // Verificar que el token esté disponible antes de hacer la llamada
    const token = localStorage.getItem('javiland_token');
    if (token) {
      fetchUsers();
    } else {
      setError('No hay token de autenticación');
      setLoading(false);
      setInitialized(true);
    }
  }, [fetchUsers]);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setError('');
    if (formData.password.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres.');
      return;
    }
    if (formData.password !== formData.passwordConfirm) {
      setError('Las contraseñas no coinciden.');
      return;
    }
    try {
      const token = localStorage.getItem('javiland_token');
      const payload = {
        email: formData.email.trim(),
        name: formData.name.trim(),
        role: formData.role,
        permissions: formData.permissions,
        password: formData.password
      };

      const response = await fetch(`${API_BASE}/admin/users`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setShowCreateModal(false);
        setFormData({
          email: '',
          name: '',
          role: 'admin',
          permissions: ['read', 'write'],
          password: '',
          passwordConfirm: '',
          newPassword: '',
          newPasswordConfirm: ''
        });
        fetchUsers();
      } else {
        const data = await response.json();
        setError(data.message || 'Error al crear usuario');
      }
    } catch (error) {
      setError('Error de conexión');
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    setError('');
    if (formData.newPassword && formData.newPassword.length < 8) {
      setError('La nueva contraseña debe tener al menos 8 caracteres.');
      return;
    }
    if (formData.newPassword && formData.newPassword !== formData.newPasswordConfirm) {
      setError('Las contraseñas nuevas no coinciden.');
      return;
    }
    try {
      const token = localStorage.getItem('javiland_token');
      const payload = {
        email: formData.email.trim(),
        name: formData.name.trim(),
        role: formData.role,
        permissions: formData.permissions
      };
      if (formData.newPassword) {
        payload.password = formData.newPassword;
      }

      const response = await fetch(`${API_BASE}/admin/users/${selectedUser.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setShowEditModal(false);
        setSelectedUser(null);
        setFormData({
          email: '',
          name: '',
          role: 'admin',
          permissions: ['read', 'write'],
          password: '',
          passwordConfirm: '',
          newPassword: '',
          newPasswordConfirm: ''
        });
        fetchUsers();
      } else {
        const data = await response.json();
        setError(data.message || 'Error al actualizar usuario');
      }
    } catch (error) {
      setError('Error de conexión');
    }
  };

  const handleDeleteUser = async () => {
    try {
      const token = localStorage.getItem('javiland_token');
      
      const response = await fetch(`${API_BASE}/admin/users/${selectedUser.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setShowDeleteModal(false);
        setSelectedUser(null);
        fetchUsers();
      } else {
        const data = await response.json();
        setError(data.message || 'Error al eliminar usuario');
      }
    } catch (error) {
      setError('Error de conexión');
    }
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    const perms = Array.isArray(user.permissions) && user.permissions.length
      ? [...user.permissions]
      : ['read', 'write'];
    setFormData({
      email: user.email,
      name: user.name,
      role: user.role,
      permissions: perms,
      password: '',
      passwordConfirm: '',
      newPassword: '',
      newPasswordConfirm: ''
    });
    setShowEditModal(true);
  };

  const openDeleteModal = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const handlePermissionChange = (permission) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter(p => p !== permission)
        : [...prev.permissions, permission]
    }));
  };

  const getRoleColor = (role) => {
    const roleObj = roles.find(r => r.value === role);
    return roleObj ? roleObj.color : 'var(--javiland-green)';
  };

  const getRoleLabel = (role) => {
    const roleObj = roles.find(r => r.value === role);
    return roleObj ? roleObj.label : role;
  };

  if (!initialized) {
    return (
      <div className="user-management-loading">
        <i className="fas fa-spinner fa-spin"></i>
        <span>Inicializando gestión de usuarios...</span>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="user-management-loading">
        <i className="fas fa-spinner fa-spin"></i>
        <span>Cargando usuarios administrativos...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="user-management-error">
        <i className="fas fa-exclamation-triangle"></i>
        <span>{error}</span>
        <button onClick={fetchUsers} className="retry-button">
          <i className="fas fa-redo"></i>
          Reintentar
        </button>
      </div>
    );
  }

  return (
    <div className="user-management">
      <div className="user-management-header">
        <div>
          <h2>👥 Gestión de Usuarios Administrativos</h2>
          <p className="user-management-hint">
            La contraseña es la que usarán para iniciar sesión en el panel (misma tabla que el login con JWT).
            Si la tabla está vacía, crea el primer usuario con el script <code>crear_admin_usuario.php</code> o desde aquí tras iniciar sesión con ese primer admin.
          </p>
        </div>
        <button 
          className="btn btn-primary"
          onClick={() => {
            setError('');
            setFormData({
              email: '',
              name: '',
              role: 'admin',
              permissions: ['read', 'write'],
              password: '',
              passwordConfirm: '',
              newPassword: '',
              newPasswordConfirm: ''
            });
            setShowCreateModal(true);
          }}
        >
          <i className="fas fa-plus"></i>
          Nuevo Usuario
        </button>
      </div>

      {error && (
        <div className="user-management-error">
          <i className="fas fa-exclamation-triangle"></i>
          <span>{error}</span>
          <button onClick={() => setError('')} className="error-close">
            <i className="fas fa-times"></i>
          </button>
        </div>
      )}

      <div className="users-grid">
        {users.map(user => (
          <div key={user.id} className="user-card">
            <div className="user-header">
              <div className="user-avatar">
                <i className="fas fa-user-circle"></i>
              </div>
              <div className="user-info">
                <h3>{user.name}</h3>
                <p className="user-email">{user.email}</p>
                <span 
                  className="user-role"
                  style={{ backgroundColor: getRoleColor(user.role) }}
                >
                  {getRoleLabel(user.role)}
                </span>
              </div>
            </div>
            
            <div className="user-permissions">
              <h4>Permisos:</h4>
              <div className="permissions-list">
                {(Array.isArray(user.permissions) ? user.permissions : []).map(permission => {
                  const perm = availablePermissions.find(p => p.value === permission);
                  return (
                    <span key={permission} className="permission-badge">
                      <i className={perm?.icon}></i>
                      {perm?.label}
                    </span>
                  );
                })}
              </div>
            </div>

            <div className="user-actions">
              <button 
                className="btn btn-secondary"
                onClick={() => openEditModal(user)}
              >
                <i className="fas fa-edit"></i>
                Editar
              </button>
              <button 
                className="btn btn-accent"
                onClick={() => openDeleteModal(user)}
                disabled={user.role === 'super_admin'}
              >
                <i className="fas fa-trash"></i>
                Eliminar
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Modal de Crear Usuario */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content user-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>➕ Crear Nuevo Usuario</h3>
              <button className="modal-close" onClick={() => setShowCreateModal(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            <form onSubmit={handleCreateUser}>
              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input
                  type="email"
                  id="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                  placeholder="usuario@javiland.com"
                />
              </div>

              <div className="form-group">
                <label htmlFor="name">Nombre Completo *</label>
                <input
                  type="text"
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  placeholder="Nombre del usuario"
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">Contraseña * (mín. 8 caracteres)</label>
                <input
                  type="password"
                  id="password"
                  autoComplete="new-password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  minLength={8}
                  placeholder="Contraseña para iniciar sesión"
                />
              </div>

              <div className="form-group">
                <label htmlFor="passwordConfirm">Confirmar contraseña *</label>
                <input
                  type="password"
                  id="passwordConfirm"
                  autoComplete="new-password"
                  value={formData.passwordConfirm}
                  onChange={(e) => setFormData({ ...formData, passwordConfirm: e.target.value })}
                  required
                  minLength={8}
                  placeholder="Repite la contraseña"
                />
              </div>

              <div className="form-group">
                <label htmlFor="role">Rol *</label>
                <select
                  id="role"
                  value={formData.role}
                  onChange={(e) => setFormData({...formData, role: e.target.value})}
                  required
                >
                  {roles.map(role => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Permisos *</label>
                <div className="permissions-checkboxes">
                  {availablePermissions.map(permission => (
                    <label key={permission.value} className="permission-checkbox">
                      <input
                        type="checkbox"
                        checked={formData.permissions.includes(permission.value)}
                        onChange={() => handlePermissionChange(permission.value)}
                      />
                      <i className={permission.icon}></i>
                      <span>{permission.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary">
                  <i className="fas fa-save"></i>
                  Crear Usuario
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Editar Usuario */}
      {showEditModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content user-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>✏️ Editar Usuario</h3>
              <button className="modal-close" onClick={() => setShowEditModal(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            <form onSubmit={handleEditUser}>
              <div className="form-group">
                <label htmlFor="edit-email">Email *</label>
                <input
                  type="email"
                  id="edit-email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                  placeholder="usuario@javiland.com"
                />
              </div>

              <div className="form-group">
                <label htmlFor="edit-name">Nombre Completo *</label>
                <input
                  type="text"
                  id="edit-name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  placeholder="Nombre del usuario"
                />
              </div>

              <div className="form-group">
                <label htmlFor="newPassword">Nueva contraseña (opcional)</label>
                <input
                  type="password"
                  id="newPassword"
                  autoComplete="new-password"
                  value={formData.newPassword}
                  onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                  minLength={8}
                  placeholder="Dejar vacío para no cambiar"
                />
              </div>

              <div className="form-group">
                <label htmlFor="newPasswordConfirm">Confirmar nueva contraseña</label>
                <input
                  type="password"
                  id="newPasswordConfirm"
                  autoComplete="new-password"
                  value={formData.newPasswordConfirm}
                  onChange={(e) => setFormData({ ...formData, newPasswordConfirm: e.target.value })}
                  minLength={8}
                  placeholder="Solo si cambias la contraseña"
                />
              </div>

              <div className="form-group">
                <label htmlFor="edit-role">Rol *</label>
                <select
                  id="edit-role"
                  value={formData.role}
                  onChange={(e) => setFormData({...formData, role: e.target.value})}
                  required
                >
                  {roles.map(role => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Permisos *</label>
                <div className="permissions-checkboxes">
                  {availablePermissions.map(permission => (
                    <label key={permission.value} className="permission-checkbox">
                      <input
                        type="checkbox"
                        checked={formData.permissions.includes(permission.value)}
                        onChange={() => handlePermissionChange(permission.value)}
                      />
                      <i className={permission.icon}></i>
                      <span>{permission.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary">
                  <i className="fas fa-save"></i>
                  Guardar Cambios
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Confirmar Eliminación */}
      {showDeleteModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>🗑️ Confirmar Eliminación</h3>
              <button className="modal-close" onClick={() => setShowDeleteModal(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            <div className="delete-content">
              <i className="fas fa-exclamation-triangle"></i>
              <p>¿Estás seguro de que quieres eliminar al usuario <strong>{selectedUser.name}</strong>?</p>
              <p className="delete-warning">Esta acción no se puede deshacer.</p>
            </div>

            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>
                Cancelar
              </button>
              <button type="button" className="btn btn-accent" onClick={handleDeleteUser}>
                <i className="fas fa-trash"></i>
                Eliminar Usuario
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
